import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import type { User } from '@/types/api/user.types';
import { profileFormSchema } from '@/schemas/profile';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useEffect } from 'react';

import { Textarea } from '../ui/textarea';
import { useUpdateUserProfileMutation } from '@/state/user';
import { toast } from 'sonner';
import type { ApiErrorResponse } from '@/types';
import { getMessageByStatus, getSource } from '@/lib';
import { useActionsTrack, useTenant } from '@/hooks';

interface UpdateProfileProps {
  user?: User | null;
  isOpen: boolean;
  onClose: () => void;
  isDismissable?: boolean;
}

export type FormValues = z.infer<typeof profileFormSchema>;

/**
 * UpdateProfile is a dialog component that allows users to update their profile information.
 * It takes the current user's profile information as a prop, and an isOpen and onClose function to handle the dialog's state.
 * The component renders a form with fields for first name, last name, address, bio, and title.
 * When the form is submitted, it calls the updateProfile mutation to update the user's profile information.
 */
const UpdateProfile = ({
  user,
  isOpen,
  onClose,
  isDismissable = true,
}: UpdateProfileProps) => {
  const { trackAction } = useActionsTrack();
  const form = useForm<FormValues>({
    resolver: zodResolver(profileFormSchema),
  });
  const { tenant } = useTenant();

  const { handleSubmit, reset, formState } = form;
  const { isDirty } = formState; // tracks if any field changed

  useEffect(() => {
    if (user) {
      reset({
        firstName: user?.profile?.first_name || '',
        lastName: user?.profile?.last_name || '',
        bio: user?.profile?.about_me || '',
        address: user?.profile?.address || '',
        title: user?.profile?.title || '',
      });
    } else {
      reset({
        firstName: '',
        lastName: '',
        address: '',
        bio: '',
      });
    }
  }, [user, form]);

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  const [updateProfile, { isLoading: updating }] =
    useUpdateUserProfileMutation();

  const onSubmit = async (data: FormValues) => {
    const updatdData = {
      first_name: data.firstName,
      last_name: data.lastName,
      about_me: data.bio,
      address: data.address,
      title: data.title,
    };
    try {
      await updateProfile({
        data: updatdData,
        tenant: tenant as string,
      }).unwrap();
      toast.success('Profile updated', {
        description: 'Your profile has been updated successfully',
      });
      onClose();
      // Tracking update action
      trackAction({
        action: 'UPDATE',
        entity: 'profile',
        itemCount: 1,
        source: getSource(),
      });
    } catch (err) {
      const error = err as ApiErrorResponse;
      toast.error(`Error`, {
        description: getMessageByStatus(error?.status || 500),
      });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => {
        if (!updating && isDismissable) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="sm:max-w-[625px]"
        showCloseButton={isDismissable}
        onEscapeKeyDown={(event) => {
          if (!isDismissable) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (!isDismissable) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader className="mb-4">
          <DialogTitle>Profile</DialogTitle>
          <DialogDescription>Update your profile</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-8 overflow-y-auto max-h-[calc(100vh-250px)]"
          >
            <div className="flex gap-6 flex-wrap md:flex-nowrap">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input placeholder="First name" {...field} />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input placeholder="Last name" {...field} />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex gap-6 flex-wrap md:flex-nowrap items-start">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input type="text" {...field} placeholder="title" />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input type="text" {...field} placeholder="address" />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="bio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bio</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Your bio" {...field} rows={5} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-6">
              {isDismissable ? (
                <Button
                  type="button"
                  variant={'outline'}
                  onClick={onClose}
                  // disabled={updating}
                >
                  Cancel
                </Button>
              ) : null}
              <Button type="submit" disabled={!isDirty}>
                Submit
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateProfile;
